import React, { useEffect, useRef, useState } from 'react';
import '../styles/MainContainer.css';
import './FlatRates.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import CustomDropdown from '../input/CustomDropdown';
import CustomTogglebox from '../input/CustomTogglebox';
import SegmentedControl from '../input/SegmentedControl';
import TextblockVariantTabs from '../textblockeditor/TextblockVariantTabs';
import OfflineBanner from '../shell/OfflineBanner';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useFlatRates from '../../hooks/useFlatRates';
import useLocations from '../../hooks/useLocations';
import {
  FLAT_RATE_AMOUNT_MAX,
  FLAT_RATE_KIND,
  FLAT_RATE_NAME_MAX,
  flatRateApiErrorMessage,
  flatRateKindIcon,
  flatRateKindLabel,
  flatRateLocationAmounts,
  formatFlatRateAmount,
  isDeduction,
  locationsWithoutFlatRateAmount,
  normalizeFlatRateKind,
} from '../../util/flatRates';
import { parsePriceInput, toPriceInput } from '../../util/eventPrices';

const EDITOR_POPUP = 'flatrateeditor';
const AMOUNT_MAX_LABEL = FLAT_RATE_AMOUNT_MAX.toLocaleString('de-DE');
// Key of the "Standard" tab — 'default' so TextblockVariantTabs shows no location icon on it.
const STANDARD_TAB = 'default';

const KIND_OPTIONS = [
  { value: FLAT_RATE_KIND.flatRate, label: 'Pauschale', icon: flatRateKindIcon(FLAT_RATE_KIND.flatRate), tone: 'accent' },
  { value: FLAT_RATE_KIND.deduction, label: 'Abzug', icon: flatRateKindIcon(FLAT_RATE_KIND.deduction), tone: 'accent' },
];

const KIND_EXPLANATION = {
  [FLAT_RATE_KIND.flatRate]: 'An eine Person oder Rolle – der Empfänger wird in der Prozessvorlage bzw. im Prozess gewählt.',
  [FLAT_RATE_KIND.deduction]: 'Externe Kosten ohne Empfänger, z. B. Feldmiete.',
};

const NAME_PLACEHOLDER = {
  [FLAT_RATE_KIND.flatRate]: 'z. B. Mailverarbeitung',
  [FLAT_RATE_KIND.deduction]: 'z. B. Feldmiete',
};

const isUsed = (flatRate) => Number(flatRate?.templateCount ?? 0) + Number(flatRate?.processCount ?? 0) > 0;

// deleteRefused: the API refused the deletion although the loaded counts were zero (assigned meanwhile)
// — set locally after a 409.
const usageLabel = (flatRate) => {
  const templates = Number(flatRate.templateCount ?? 0);
  const processes = Number(flatRate.processCount ?? 0);
  const parts = [];
  if (templates > 0) parts.push(templates === 1 ? '1 Prozessvorlage' : `${templates} Prozessvorlagen`);
  if (processes > 0) parts.push(processes === 1 ? '1 Prozess' : `${processes} Prozesse`);
  if (parts.length > 0) return parts.join(' · ');
  return flatRate.deleteRefused ? 'In Verwendung' : 'Nicht genutzt';
};

// "3 Pauschalen", "1 Abzug" — '' for 0.
const countLabel = (count, kind) => (count > 0 ? `${count} ${flatRateKindLabel(kind, { plural: count !== 1 })}` : '');

// "Der Abzug" / "Die Pauschale" (sentence start).
const kindWithArticle = (kind) => (normalizeFlatRateKind(kind) === FLAT_RATE_KIND.deduction ? 'Der Abzug' : 'Die Pauschale');

// Popup form of a catalog entry (null = new). Amounts are input strings, `omitted` = "entfällt" (amount null).
// deviations: saved ones by location name, then new ones (isNew; locationId null until a location is chosen).
function formFromEntry(entry) {
  return {
    kind: normalizeFlatRateKind(entry?.kind),
    name: entry?.name ?? '',
    amount: toPriceInput(entry?.amount),
    omitted: !!entry && entry.amount == null,
    deviations: flatRateLocationAmounts(entry).map(a => ({
      key: `loc-${a.locationId}`,
      locationId: a.locationId,
      locationName: a.locationName ?? '',
      amount: toPriceInput(a.amount),
      omitted: a.amount == null,
      isNew: false,
    })),
  };
}

function amountError(input) {
  const amount = parsePriceInput(input);
  if (amount == null) return 'Bitte einen Betrag angeben.';
  if (Number.isNaN(amount) || amount > FLAT_RATE_AMOUNT_MAX) return `Der Betrag muss zwischen 0 und ${AMOUNT_MAX_LABEL} € liegen.`;
  return null;
}

// Client checks mirroring FlatRateService (same texts); the server validates again.
// → { name?, tabs: { [tabKey]: { amount?, location? } } }
function validateForm(form, duplicateName) {
  const errors = { tabs: {} };
  const name = form.name.trim();
  if (!name) errors.name = 'Bitte einen Namen angeben.';
  else if (name.length > FLAT_RATE_NAME_MAX) errors.name = `Der Name darf höchstens ${FLAT_RATE_NAME_MAX} Zeichen lang sein.`;
  else if (duplicateName) errors.name = 'Es gibt bereits eine Pauschale oder einen Abzug mit diesem Namen.';
  const standardError = form.omitted ? null : amountError(form.amount);
  if (standardError) errors.tabs[STANDARD_TAB] = { amount: standardError };
  form.deviations.forEach(d => {
    if (d.locationId == null) {
      errors.tabs[d.key] = { location: 'Bitte eine Location wählen.' };
      return;
    }
    const error = d.omitted ? null : amountError(d.amount);
    if (error) errors.tabs[d.key] = { amount: error };
  });
  return errors;
}

const hasErrors = (errors) => !!errors.name || Object.keys(errors.tabs).length > 0;

function deleteErrorMessage(err, kind) {
  // 404 bodies are ProblemDetails (English title) — use our own text.
  if (err?.response?.status === 404) return `${kindWithArticle(kind)} existiert nicht mehr.`;
  const fallback = err?.response?.status === 409
    ? `${kindWithArticle(kind)} wird noch von Prozessvorlagen oder Prozessen verwendet und kann nicht gelöscht werden.`
    : 'Löschen fehlgeschlagen.';
  return flatRateApiErrorMessage(err, fallback);
}

/**
 * Admin page /flatrates (quick-access tile "Pauschalen & Abzüge" on the home page): the catalog of flat
 * rates (paid to a person or role) and deductions (external costs, no recipient) — each with a standard
 * amount plus optional deviations per location ("entfällt" possible) — and usage counts. Process templates
 * assign entries ("Standard-Pauschalen/-Abzüge"); processes copy them with the amount of their location at
 * creation. Create/edit via a popup (amount tabs like the textblock variants), delete only for unused entries
 * (the API refuses referenced ones and says where). Loads /FlatRate/GetAll once, the locations only on the
 * first "+ Abweichung"; after saves/deletes the list is patched locally.
 */
function FlatRates() {
  const { flatRates, loaded, loading, failed, ensureLoaded, upsert, remove } = useFlatRates();
  const {
    locations,
    loaded: locationsLoaded,
    failed: locationsFailed,
    ensureLoaded: ensureLocations,
  } = useLocations();
  const { isOffline, onlineEpoch } = useOffline();
  const { isPopupOpen, popupData, openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  // Edited row (null = create). Kept after closing so the popup keeps its title during the exit animation.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => formFromEntry(null));
  const [activeTab, setActiveTab] = useState(STANDARD_TAB);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const editorOpen = isPopupOpen && popupData.name === EDITOR_POPUP;
  const editorOpenRef = useRef(editorOpen);
  editorOpenRef.current = editorOpen;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const saveRequestRef = useRef(0);
  const editorSessionRef = useRef(0); // +1 per opening of the editor
  const newDeviationSeqRef = useRef(0); // keys of new deviation tabs

  // onlineEpoch: retry after "Erneut verbinden" if the first load failed (ensureLoaded is a no-op once loaded).
  useEffect(() => {
    if (!isOffline) ensureLoaded();
  }, [ensureLoaded, isOffline, onlineEpoch]);

  // Fresh form on every open, prefilled from the edited row.
  useEffect(() => {
    if (!editorOpen) return;
    setForm(formFromEntry(editingRef.current));
    setActiveTab(STANDARD_TAB);
    setSubmitted(false);
    setError('');
  }, [editorOpen]);

  // Locations only for "+ Abweichung": requested on the click, and again once a connection is back when the
  // click happened offline (ensureLoaded is a no-op once requested or loaded). Only while the editor is open —
  // a draft left from a cancelled edit is reset on the next open anyway.
  const hasPendingDeviation = form.deviations.some(d => d.locationId == null);
  useEffect(() => {
    if (editorOpen && hasPendingDeviation && !isOffline) ensureLocations();
  }, [editorOpen, hasPendingDeviation, isOffline, ensureLocations]);

  // The locations arrived after "+ Abweichung" was clicked: preselect the first free one for the new tab.
  useEffect(() => {
    if (!locationsLoaded) return;
    setForm(prev => {
      const pending = prev.deviations.find(d => d.locationId == null);
      const first = pending ? locationsWithoutFlatRateAmount(prev.deviations, locations)[0] : null;
      if (!first) return prev;
      return {
        ...prev,
        deviations: prev.deviations.map(d =>
          (d.key === pending.key ? { ...d, locationId: first.id, locationName: first.name ?? '' } : d)),
      };
    });
  }, [locationsLoaded, locations]);

  const trimmedName = form.name.trim();
  const duplicateName = !!trimmedName && flatRates.some(f =>
    f.id !== editing?.id && (f.name ?? '').trim().toLocaleLowerCase('de') === trimmedName.toLocaleLowerCase('de'));
  const errors = validateForm(form, duplicateName);
  // The duplicate-name hint shows while typing, the other errors after the first save attempt.
  const shownErrors = submitted ? errors : { tabs: {}, ...(duplicateName ? { name: errors.name } : {}) };

  const editingUsed = !!editing && (isUsed(editing) || !!editing.deleteRefused);
  // The server refuses a kind change of a used entry.
  const kindLocked = editingUsed;

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setError('');
  };

  const setDeviation = (key, patch) => {
    setForm(prev => ({ ...prev, deviations: prev.deviations.map(d => (d.key === key ? { ...d, ...patch } : d)) }));
    setError('');
  };

  const openEditor = (flatRate = null) => {
    setEditing(flatRate);
    editorSessionRef.current += 1;
    openPopup(EDITOR_POPUP, flatRate?.id ?? 'new');
  };

  // ── Amount tabs: Standard · <Ort> · … · + Abweichung ──────────────────────────────────────────────────
  const activeDeviation = form.deviations.find(d => d.key === activeTab) ?? null;
  const shownTab = activeDeviation ? activeTab : STANDARD_TAB;
  const amountTabs = [
    { key: STANDARD_TAB, label: 'Standard', hasError: !!shownErrors.tabs[STANDARD_TAB] },
    ...form.deviations.map(d => ({
      key: d.key,
      label: d.locationName || 'Neue Abweichung',
      isNew: d.isNew,
      hasError: !!shownErrors.tabs[d.key],
    })),
  ];

  // Once the locations are known: no location left for another deviation (every one has one, or none exist) —
  // "+ Abweichung" is disabled then and says why (before the first click the locations are not loaded yet).
  const noFreeLocation = locationsLoaded && locationsWithoutFlatRateAmount(form.deviations, locations).length === 0;
  const noFreeLocationText = locations.length === 0
    ? 'Es sind noch keine Locations angelegt – das geht auf der Seite „Locations“ (Startseite → Verwaltung).'
    : 'Für jede Location gibt es schon eine Abweichung.';

  // Adds a new deviation tab (amount prefilled with the standard), or opens the one still without location.
  const handleAddDeviation = () => {
    if (!isOffline) ensureLocations();
    const pending = form.deviations.find(d => d.locationId == null);
    if (pending) {
      setActiveTab(pending.key);
      return;
    }
    if (noFreeLocation) return;
    const first = locationsWithoutFlatRateAmount(form.deviations, locations)[0] ?? null;
    newDeviationSeqRef.current += 1;
    const key = `new-${newDeviationSeqRef.current}`;
    setForm(prev => ({
      ...prev,
      deviations: [...prev.deviations, {
        key,
        locationId: first?.id ?? null,
        locationName: first?.name ?? '',
        amount: prev.omitted ? '' : prev.amount,
        omitted: false,
        isNew: true,
      }],
    }));
    setActiveTab(key);
    setError('');
  };

  // Removed from the form only — deleted with "Speichern".
  const removeDeviation = (key) => {
    setForm(prev => ({ ...prev, deviations: prev.deviations.filter(d => d.key !== key) }));
    setActiveTab(STANDARD_TAB);
    setError('');
  };

  const handleLocationChange = (key, value) => {
    const location = locations.find(l => String(l.id) === String(value)) ?? null;
    setDeviation(key, { locationId: location?.id ?? null, locationName: location?.name ?? '' });
  };

  const handleSave = () => {
    if (saving || isOffline) return;
    setSubmitted(true);
    if (hasErrors(errors)) {
      // Show the first tab with an error unless the open one has one.
      if (!errors.tabs[shownTab]) {
        const firstInvalid = amountTabs.find(t => errors.tabs[t.key]);
        if (firstInvalid) setActiveTab(firstInvalid.key);
      }
      return;
    }
    const target = editing;
    const kindLabel = flatRateKindLabel(form.kind);
    const body = {
      name: trimmedName,
      kind: form.kind,
      amount: form.omitted ? null : parsePriceInput(form.amount),
      locationAmounts: form.deviations.map(d => ({
        locationId: d.locationId,
        amount: d.omitted ? null : parsePriceInput(d.amount),
      })),
    };
    // The editor may be cancelled and reopened (for another row) while saving: only touch it if it still
    // shows this request's target.
    const request = ++saveRequestRef.current;
    const session = editorSessionRef.current;
    const stillEditing = () => editorOpenRef.current && editorSessionRef.current === session;
    setSaving(true);
    setError('');
    startSuccessBarLoading(`${kindLabel} wird gespeichert`);
    (target ? api.put(`/FlatRate/Update/${target.id}`, body) : api.post('/FlatRate/Create', body))
      .then(res => {
        upsert(res.data);
        startSuccessBarSuccess(`${kindLabel} wurde gespeichert`);
        if (stillEditing()) closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        // Deleted meanwhile (e.g. by another admin): drop the row.
        if (target && err?.response?.status === 404) remove(target.id);
        if (!stillEditing()) return;
        setError(target && err?.response?.status === 404
          ? `${kindWithArticle(target.kind)} existiert nicht mehr.`
          : flatRateApiErrorMessage(err, `${kindWithArticle(form.kind)} konnte nicht gespeichert werden.`));
      })
      .finally(() => { if (saveRequestRef.current === request) setSaving(false); });
  };

  const askDelete = (flatRate) => {
    setPendingDelete(flatRate);
    openPopup('confirmdeleteflatrate', flatRate.id);
  };

  const handleConfirmDelete = () => {
    const flatRate = pendingDelete;
    closePopup();
    if (!flatRate) return;
    const kindLabel = flatRateKindLabel(flatRate.kind);
    startSuccessBarLoading(`${kindLabel} wird gelöscht`);
    api.delete(`/FlatRate/Delete/${flatRate.id}`)
      .then(() => {
        remove(flatRate.id);
        startSuccessBarSuccess(`${kindLabel} gelöscht`);
      })
      .catch(err => {
        closeSuccessBar();
        // Changed meanwhile (e.g. by another admin): bring the row in line with the server.
        const status = err?.response?.status;
        if (status === 404) remove(flatRate.id);
        // Still assigned (the message says where): the row no longer offers deleting.
        if (status === 409) upsert({ ...flatRate, deleteRefused: true });
        setDeleteError(deleteErrorMessage(err, flatRate.kind));
        openPopup('flatratedeleteerror', flatRate.id);
      });
  };

  const flatRateEntries = flatRates.filter(f => !isDeduction(f));
  const deductionEntries = flatRates.filter(f => isDeduction(f));
  const counts = [
    countLabel(flatRateEntries.length, FLAT_RATE_KIND.flatRate),
    countLabel(deductionEntries.length, FLAT_RATE_KIND.deduction),
  ].filter(Boolean);

  const subtitle = !loaded
    ? (loading ? 'Lädt …' : '')
    : flatRates.length === 0
      ? 'Noch keine Pauschalen oder Abzüge angelegt'
      : `${counts.join(' · ')} · Löschen nur bei ungenutzten`;

  const renderRow = (f) => {
    const unused = !isUsed(f) && !f.deleteRefused;
    const deviations = flatRateLocationAmounts(f);
    return (
      <li key={f.id} className="flatRatesRow">
        <span className="material-symbols-rounded flatRatesIcon" aria-hidden="true">{flatRateKindIcon(f.kind)}</span>
        <span className="flatRatesText">
          <span className="flatRatesName">{f.name}</span>
          <span className="flatRatesAmount">
            {deviations.length > 0 ? `Standard: ${formatFlatRateAmount(f.amount)}` : formatFlatRateAmount(f.amount)}
          </span>
          {deviations.length > 0 && (
            <span className="flatRatesDeviations" title="Abweichungen je Location">
              <span className="material-symbols-rounded" aria-hidden="true">location_on</span>
              <span>
                {deviations.map(d => `${d.locationName}: ${formatFlatRateAmount(d.amount)}`).join(' · ')}
              </span>
            </span>
          )}
        </span>
        <span className={`flatRatesUsage${unused ? ' isUnused' : ''}`}>{usageLabel(f)}</span>
        <span className="flatRatesActions">
          <button
            type="button"
            className="flatRatesBtn"
            onClick={() => openEditor(f)}
            disabled={isOffline}
            aria-label={`${f.name} bearbeiten`}
            title={isOffline ? 'Offline nicht verfügbar' : 'Bearbeiten'}
          >
            <span className="material-symbols-rounded" aria-hidden="true">edit</span>
          </button>
          {unused && (
            <button
              type="button"
              className="flatRatesBtn isDanger"
              onClick={() => askDelete(f)}
              disabled={isOffline}
              aria-label={`${f.name} löschen`}
              title={isOffline
                ? 'Offline nicht verfügbar'
                : `Löschen (nur ungenutzte ${flatRateKindLabel(f.kind, { plural: true })})`}
            >
              <span className="material-symbols-rounded" aria-hidden="true">delete</span>
            </button>
          )}
        </span>
      </li>
    );
  };

  const renderSection = (kind, entries) => {
    const title = flatRateKindLabel(kind, { plural: true });
    return (
      <section className="flatRatesSection" aria-label={title}>
        <h3 className="flatRatesSectionTitle">{title}</h3>
        {entries.length === 0
          ? <p className="flatRatesSectionEmpty">Noch keine {title}.</p>
          : <ul className="flatRatesList">{entries.map(renderRow)}</ul>}
      </section>
    );
  };

  let content;
  if (isOffline && !loaded) {
    content = <p className="flatRatesMessage">Offline nicht verfügbar.</p>;
  } else if (!loaded) {
    // `failed` instead of !loading: before the first request starts, loading is still false.
    content = failed ? <p className="flatRatesMessage">Pauschalen und Abzüge konnten nicht geladen werden.</p> : null;
  } else {
    content = (
      <React.Fragment>
        {renderSection(FLAT_RATE_KIND.flatRate, flatRateEntries)}
        {renderSection(FLAT_RATE_KIND.deduction, deductionEntries)}
      </React.Fragment>
    );
  }

  // Location of a new deviation tab: dropdown of the free locations, or why there is none (inline).
  const renderLocationPicker = (d) => {
    let picker;
    if (!locationsLoaded) {
      if (isOffline) {
        picker = <p className="formFieldHint">Locations sind offline nicht verfügbar.</p>;
      } else if (locationsFailed) {
        picker = (
          <div className="flatRatesInlineState">
            <p className="formFieldHint">Locations konnten nicht geladen werden.</p>
            <ActionButton icon="refresh" label="Erneut laden" onClick={() => ensureLocations()} />
          </div>
        );
      } else {
        picker = <p className="formFieldHint">Locations werden geladen …</p>;
      }
    } else if (locations.length === 0) {
      picker = (
        <p className="formFieldHint">
          Es sind noch keine Locations angelegt – das geht auf der Seite „Locations“ (Startseite → Verwaltung).
        </p>
      );
    } else {
      const free = locationsWithoutFlatRateAmount(form.deviations.filter(x => x.key !== d.key), locations);
      picker = free.length === 0
        ? <p className="formFieldHint">Für jede Location gibt es schon eine Abweichung.</p>
        : (
          <CustomDropdown
            icon="location_on"
            ariaLabel="Location"
            value={d.locationId == null ? '' : String(d.locationId)}
            onChange={e => handleLocationChange(d.key, e.target.value)}
          >
            <option value="" disabled>– Location wählen –</option>
            {free.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
          </CustomDropdown>
        );
    }
    return (
      <React.Fragment>
        <h3>Location</h3>
        {picker}
        {shownErrors.tabs[d.key]?.location && (
          <p className="flatRatesFieldError">{shownErrors.tabs[d.key].location}</p>
        )}
      </React.Fragment>
    );
  };

  // Amount + "entfällt" of the open tab (Standard or a location).
  const renderAmountFields = ({ key, amount, omitted, omittedLabel, onAmountChange, onOmittedChange }) => {
    const amountMessage = shownErrors.tabs[key]?.amount;
    return (
      <React.Fragment>
        <div className="formFieldWrapper">
          <label className="formFieldLabel" htmlFor={`flatRateAmount-${key}`}>Betrag (€)</label>
          <input
            id={`flatRateAmount-${key}`}
            type="number"
            min={0}
            max={FLAT_RATE_AMOUNT_MAX}
            step={0.01}
            inputMode="decimal"
            placeholder={omitted ? 'entfällt' : '0,00'}
            value={amount}
            disabled={omitted}
            onChange={e => onAmountChange(e.target.value)}
            aria-invalid={amountMessage ? 'true' : undefined}
            className={amountMessage ? 'isInvalid' : ''}
          />
        </div>
        {amountMessage && <p className="flatRatesFieldError">{amountMessage}</p>}
        <CustomTogglebox
          placeholder={omittedLabel}
          checked={omitted}
          onChange={e => onOmittedChange(e.target.checked)}
        />
      </React.Fragment>
    );
  };

  const renderTabPanel = () => {
    if (!activeDeviation) {
      return (
        <React.Fragment key={STANDARD_TAB}>
          {renderAmountFields({
            key: STANDARD_TAB,
            amount: form.amount,
            omitted: form.omitted,
            omittedLabel: 'Entfällt im Standard – gilt nur an Locations mit eigener Abweichung',
            onAmountChange: value => setField('amount', value),
            onOmittedChange: value => setField('omitted', value),
          })}
          {form.omitted && form.deviations.length === 0 && (
            <p className="formFieldHint">Ohne Abweichung gilt der Eintrag damit an keiner Location.</p>
          )}
        </React.Fragment>
      );
    }
    const d = activeDeviation;
    return (
      <React.Fragment key={d.key}>
        {d.isNew && renderLocationPicker(d)}
        {d.locationId != null && renderAmountFields({
          key: d.key,
          amount: d.amount,
          omitted: d.omitted,
          omittedLabel: 'Entfällt an dieser Location',
          onAmountChange: value => setDeviation(d.key, { amount: value }),
          onOmittedChange: value => setDeviation(d.key, { omitted: value }),
        })}
        <div className="flatRatesPanelActions">
          <ActionButton icon="delete" label="Abweichung entfernen" type={1} onClick={() => removeDeviation(d.key)} />
        </div>
      </React.Fragment>
    );
  };

  const pendingDeleteDeviations = flatRateLocationAmounts(pendingDelete);
  const pendingDeleteIsDeduction = isDeduction(pendingDelete);

  return (
    <React.Fragment>
      <main className="defaultMain">
        <OfflineBanner />
        <div className="flatRatesHeader">
          <div className="flatRatesHeaderText">
            <h2 className="flatRatesTitle">Pauschalen &amp; Abzüge</h2>
            {subtitle && <span className="flatRatesSubtitle">{subtitle}</span>}
          </div>
          <ActionButton
            icon="add"
            label="Neuer Eintrag"
            type={3}
            onClick={() => openEditor()}
            disabled={isOffline}
          />
        </div>
        <p className="formFieldHint">
          Pauschalen gehen an Personen oder Rollen, Abzüge sind externe Kosten (z. B. Feldmiete). Beide werden in den
          Prozessvorlagen und je Prozess zugeordnet, sind je Spieltag fällig und werden in der Statistik vom Netto
          abgezogen. Beträge: ein Standard für alle Locations, optional mit Abweichung je Location.
        </p>
        {content}
      </main>

      <Popup
        name={EDITOR_POPUP}
        headerText={editing ? `${flatRateKindLabel(form.kind)} bearbeiten` : 'Neuer Eintrag'}
        confirmText={saving ? 'Speichert …' : 'Speichern'}
        type="info"
        onConfirm={handleSave}
        dontCloseOnConfirm
        withCancel
        wide
        confirmDisabled={saving || isOffline}
      >
        <div className="flatRatesForm">
          <h3>Art</h3>
          <fieldset className="flatRatesKind" disabled={kindLocked}>
            <SegmentedControl
              options={KIND_OPTIONS}
              value={form.kind}
              onChange={value => setField('kind', value)}
              ariaLabel="Art"
            />
          </fieldset>
          <p className="formFieldHint">{KIND_EXPLANATION[form.kind]}</p>
          {kindLocked && <p className="formFieldHint">Die Art ist fest, solange der Eintrag verwendet wird.</p>}

          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="flatRateName">Name</label>
            <input
              id="flatRateName"
              type="text"
              maxLength={FLAT_RATE_NAME_MAX}
              placeholder={NAME_PLACEHOLDER[form.kind]}
              autoComplete="off"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              aria-invalid={shownErrors.name ? 'true' : undefined}
              className={shownErrors.name ? 'isInvalid' : ''}
            />
          </div>
          {shownErrors.name && <p className="flatRatesFieldError">{shownErrors.name}</p>}

          <h3>Betrag</h3>
          <TextblockVariantTabs
            tabs={amountTabs}
            activeKey={shownTab}
            onSelect={setActiveTab}
            onAdd={handleAddDeviation}
            ariaLabel="Beträge je Location"
            addLabel="Abweichung"
            addDisabled={saving || noFreeLocation}
            addTitle={noFreeLocation ? noFreeLocationText : undefined}
            idPrefix="flatRateAmount"
          />
          <p className="formFieldHint">
            Neue Prozesse an einer Location übernehmen deren Betrag, sonst den Standard. Bestehende Prozesse
            behalten ihre Beträge.
          </p>
          {/* A tab still without location explains it in its own panel. */}
          {noFreeLocation && !hasPendingDeviation && <p className="formFieldHint">{noFreeLocationText}</p>}
          <div
            className="flatRatesAmountPanel"
            id="flatRateAmountPanel"
            role="tabpanel"
            aria-labelledby={`flatRateAmountTab-${shownTab}`}
          >
            {renderTabPanel()}
          </div>

          {editingUsed && (
            <div className="flatRatesNotice">
              <span className="material-symbols-rounded" aria-hidden="true">info</span>
              <span>
                Ein geänderter Betrag oder eine geänderte Abweichung gilt nur für Prozesse, die danach angelegt
                werden. Bestehende Prozesse behalten ihren übernommenen Betrag – er lässt sich dort anpassen. Ein
                neuer Name erscheint dagegen überall, auch in bestehenden Prozessen und der Statistik.
              </span>
            </div>
          )}
          {isOffline && (
            <div className="formOfflineHint">
              <span className="material-symbols-rounded formOfflineHintIcon" aria-hidden="true">cloud_off</span>
              Offline – Speichern nicht verfügbar
            </div>
          )}
          {error && <div className="newError" role="alert">{error}</div>}
        </div>
      </Popup>

      <Popup
        name="confirmdeleteflatrate"
        headerText={`${flatRateKindLabel(pendingDelete?.kind)} löschen`}
        confirmText="Löschen"
        type="confirmaction"
        onConfirm={handleConfirmDelete}
        withCancel
      >
        <p>
          Soll {pendingDeleteIsDeduction ? 'der Abzug' : 'die Pauschale'} <b>{pendingDelete?.name}</b> dauerhaft
          gelöscht werden?
        </p>
        {pendingDeleteDeviations.length > 0 && (
          <p className="formFieldHint">
            {pendingDeleteIsDeduction ? 'Seine' : 'Ihre'} Abweichungen je Location werden mitgelöscht:{' '}
            {pendingDeleteDeviations.map(d => d.locationName).join(', ')}.
          </p>
        )}
        <p className="formFieldHint">
          Nicht möglich, solange eine Prozessvorlage oder ein Prozess {pendingDeleteIsDeduction ? 'ihn' : 'sie'} noch
          verwendet.
        </p>
      </Popup>
      <Popup name="flatratedeleteerror" headerText="Löschen fehlgeschlagen" confirmText="Ok" type="error">
        <p>{deleteError}</p>
      </Popup>
    </React.Fragment>
  );
}

export default FlatRates;
